<template>
    <component :is="innerMap ? 'div' : PublicProfileLayout" :role="innerMap ? undefined : role">
        <div class="public-profile-wrapper">
            <div class="surface-section min-h-screen ">
                <div>
                    <div class="banner-responsive" :style="bannerStyleObject"></div>

                    <div class="overlap-section col-12 md:col-10 flex justify-content-center align-items-center">
                        <div
                            class="flex flex-column md:flex-row align-items-center justify-content-between w-full mt-3 gap-3">
                            <div class="flex flex-column md:flex-row align-items-center gap-3 text-center md:text-left">
                                <img :src="profileImage" class="profile-image" alt="Profile" />
                                <div class="profile-info">
                                    <h1 class="H5-SemiBold mb-1">{{ profileName }}</h1>
                                    <p class="caption mb-0">{{ profileTitle }}</p>
                                </div>
                            </div>
                             <a :href="profile?.email ? `mailto:${profile.email}?subject=Contact%20Request&body=Hello` : 'mailto:support@ilearndsl.com?subject=Contact%20Request&body=Hello'"
                                target="_blank" rel="noopener">
                            <Button class="orange-button-small flex flex-wrap gap-3 md:w-auto" icon="pi pi-comments"
                                label="Contact Me" />
                                </a>
                        </div>
                    </div>
                </div>
                <div class="flex flex-column px-4 lg:flex-row gap-4 mt-4 justify-content-center">
                    <div class="w-full lg:w-4 p-4">
                        <div class="mb-4">
                            <h3 class="Paragraph-Medium font-semibold mb-3">Personal Information</h3>
                            <ul class="list-none p-0 m-0">
                                <li class="flex align-items-start gap-2 mb-3" v-if="profile?.age">
                                    <i class="pi pi-calendar text-500 mt-1"></i>
                                    <div class="flex flex-column">
                                        <span class="Paragraph-Medium mb-1">Age:</span>
                                        <span>{{ profile.age }} years</span>
                                    </div>
                                </li>
                                <li class="flex align-items-start gap-2 mb-3" v-if="profile?.language">
                                    <i class="pi pi-globe text-500 mt-1"></i>
                                    <div class="flex flex-column">
                                        <span class="Paragraph-Medium mb-1">Language:</span>
                                        <span>{{ capitalize(profile.language) }}</span>
                                    </div>
                                </li>
                                <li class="flex align-items-start gap-2 mb-3" v-if="profile?.city">
                                    <i class="pi pi-map-marker text-500 mt-1"></i>
                                    <div class="flex flex-column">
                                        <span class="Paragraph-Medium mb-1">Location:</span>
                                        <span>{{ capitalize(profile.city) }}</span>
                                    </div>
                                </li>
                                <li class="flex align-items-start gap-2 mb-3">
                                    <i class="pi pi-flag text-500 mt-1"></i>
                                    <div class="flex flex-column">
                                        <span class="Paragraph-Medium mb-1">Nationality:</span>
                                        <span>{{ profile?.nationality || 'Sri Lanka' }}</span>
                                    </div>
                                </li>
                                <li class="flex align-items-start gap-2 mb-3" v-if="profile?.currentInstitution">
                                    <i class="pi pi-building text-500 mt-1"></i>
                                    <div class="flex flex-column">
                                        <span class="Paragraph-Medium mb-1">School:</span>
                                        <span>{{ capitalize(profile.currentInstitution) }}</span>
                                    </div>
                                </li>
                                <li class="flex align-items-start gap-2 mb-3" v-if="profile?.currentEducationLevel">
                                    <i class="pi pi-star text-500 mt-1"></i>
                                    <div class="flex flex-column">
                                        <span class="Paragraph-Medium mb-1">Grade:</span>
                                        <span>{{ cleanupLables(profile.currentEducationLevel) }}</span>
                                    </div>
                                </li>
                            </ul>
                        </div>
                        <div class="mb-6" v-if="profile?.preferredLearningMode || profile?.preferredClassType">
                            <h3 class="font-semibold Paragraph-Medium mb-3">Learning Preferences</h3>
                            <ul class="list-none p-0 m-0 mb-3">
                                <li v-if="profile?.preferredLearningMode?.length" class="mb-2 Paragraph-Medium">
                                    Class Type: {{ profile.preferredLearningMode.map(cleanupLables).join(' | ') }}
                                </li>
                                <li v-if="profile?.preferredClassType?.length" class="Paragraph-Medium">
                                    Class Model: {{ profile.preferredClassType.map(cleanupLables).join(' | ') }}
                                </li>
                            </ul>
                        </div>
                        <div>
                            <h3 class="font-semibold Paragraph-Medium mb-3">Preferred Subjects</h3>
                            <ul class="list-none p-0 m-0 flex flex-wrap gap-2">
                                <li v-for="(subject, index) in preferredSubjects" :key="index"
                                    class="border-1 border-300 border-round-3xl bg-transparent py-2 px-3 text-sm">
                                    {{ cleanupLables(subject) }}
                                </li>
                            </ul>
                        </div>
                    </div>
                    <Divider layout="vertical" />
                    <div class="w-full lg:w-6 p-4">
                        <div class="mb-4">
                            <h3 class="font-semibold Paragraph-Medium mb-3">About Me</h3>
                            <p class="line-height-3 Label m-0">
                                {{ profile?.bio || "No description provided." }}.
                            </p>
                        </div>

                        <div>
                            <h3 class="font-semibold Paragraph-Medium mb-3">Registered Tutors</h3>
                            <div class="grid">
                                <div v-for="(tutor, index) in enrolledTutors" :key="tutor.tutorId || index"
                                    class="col-12 sm:col-6 md:col-4 mb-3 flex">
                                    <div class="enrolled-card border-round-lg overflow-hidden shadow-2 hover:shadow-4 transition-all transition-duration-200 flex flex-column w-full h-full">
                                        <div class="enrolled-card-image surface-100 flex align-items-center justify-content-center">
                                            <img :src="tutor.image" class="enrolled-card-img" :alt="tutor.name" />
                                        </div>
                                        <div class="p-3 flex flex-column justify-content-between flex-grow-1 gap-3">
                                            <div>
                                                <h4 class="H6-Medium mb-1 enrolled-tutor-name" :title="tutor.name">{{ tutor.name }}</h4>
                                                <p class="Caption mb-1 mt-0">{{ cleanupLables(tutor.tutorType) || 'Tutor' }}</p>
                                                <p class="Caption mb-2 mt-0 text-color-secondary enrolled-tutor-lang">
                                                    {{ tutor.teachingLanguage.map(cleanupLables).join(' / ') }}
                                                    {{ tutor.teachingLanguage.length === 1 ? 'Medium' : 'Mediums' }}
                                                </p>
                                                <div class="flex flex-wrap gap-1">
                                                    <span v-for="subject in tutor.subjects.slice(0, 3)" :key="subject" class="enrolled-subject-chip">
                                                        {{ cleanupLables(subject) }}
                                                    </span>
                                                    <span v-if="tutor.subjects.length > 3" class="enrolled-subject-chip">
                                                        +{{ tutor.subjects.length - 3 }} more
                                                    </span>
                                                </div>
                                            </div>
                                            <!-- <div class="enrolled-card-bottom">
                                                <div v-if="tutor.nextClassDate" class="flex align-items-center gap-1 text-xs text-color-secondary mb-1">
                                                    <i class="pi pi-calendar" style="font-size:0.75rem"></i>
                                                    <span>Next: {{ tutor.nextClassDate }} {{ tutor.nextClassStart }}</span>
                                                </div>
                                                <div class="flex flex-wrap gap-1">
                                                    <span v-for="model in tutor.classModels" :key="model" class="enrolled-subject-chip">
                                                        {{ cleanupLables(model) }}
                                                    </span>
                                                </div>
                                            </div> -->
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </component>
</template>

<script setup>
import { ref, computed, onMounted, watchEffect, defineProps} from "vue";
import { useRoute } from 'vue-router';
import * as api from '@/api/student';
import bannerImg from "@/assets/Images/studentCoverProfile.png";
import Button from 'primevue/button';
import Divider from 'primevue/divider';
import PublicProfileLayout from "@/layout/PublicProfileLayout.vue";
import { useAuthStore } from '@/stores/authStore';
import { generateSignedUrl } from '@/api/storage';
import blankProfile from '@/assets/blank-profile.png';
import { capitalize } from "@/utils/cleanups";
import { notifyError } from "@/utils/notification";
import { cleanupLables } from "@/utils/cleanups";

const props = defineProps({
    studentId: {
        type: String,
        default: null
    },
    innerMap: {
        type: Boolean,
        default: false
    }
});

const route = useRoute();
const authStore = useAuthStore();
const profile = ref(null);
const isLoading = ref(true);
const error = ref(null);
const bannerImgRef = ref(bannerImg);

const profileImage = ref(blankProfile);

watchEffect(async () => {
    if (profile.value?.profilePhotoURL) {
        profileImage.value = await generateSignedUrl(profile.value.profilePhotoURL);
    } else {
        profileImage.value = blankProfile;
    }
}); 

const profileName = computed(() => profile.value?.username);
const profileTitle = computed(() => {
    if (!profile.value) return '';
    const types = profile.value.preferredClassType;
    if (!Array.isArray(types) || types.length === 0) return '';
    return `I prefer ${types.map(cleanupLables).join(' & ')} classes`;
});

const bannerStyleObject = computed(() => ({
    backgroundImage: `url(${bannerImgRef.value})`,
    backgroundSize: "cover",
    backgroundPosition: "center",
    backgroundRepeat: "no-repeat",
    width: "100%",
}));
const role = computed(() => authStore.user?.role);

const fetchProfile = async (id) => {
    isLoading.value = true;
    error.value = null;
    try {
        const res = await api.publicProfile(id);
        profile.value = res.data.data;
    } catch (err) {
        console.error('Failed to fetch profile:', err);
        error.value = 'Failed to load profile. Please try again.';
        notifyError('Error', 'Failed to load profile data. Please try again.');
    } finally {
        isLoading.value = false;
    }
};

const enrolledTutors = ref([]);
const fetchEnrolledTutors = async (studentId) => {
    try {
        const res = await api.publicEnrolledTutors(studentId);
        const list = res.data?.data ?? [];

        enrolledTutors.value = list.map((t) => ({
            tutorId: t.tutorId,
            name: t.tutorName,
            tutorType: t.tutorType,
            location: t.address,
            teachingLanguage: t.teachingLanguage ?? [],
            subjects: t.subjects ?? [],
            classModels: t.classModels ?? [],
            nextClassDate: t.nextClassDate ?? null,
            nextClassStart: t.nextClassStart ?? null,
            rawPhoto: t.tutorPhotoURL,
            image: blankProfile,
        }));

        // Try to generate signed URL for each tutor photo
        enrolledTutors.value.forEach((tutor, idx) => {
            if (tutor.rawPhoto) {
                generateSignedUrl(tutor.rawPhoto)
                    .then((url) => {
                        enrolledTutors.value[idx].image = url;
                    })
                    .catch(() => {});
            }
        });
    } catch (err) {
        console.error('Failed to fetch enrolled tutors:', err);
    }
};

// Initialize on component mount
onMounted(() => {
    const id = props.studentId || route.params.id;
    if (id) {
        fetchProfile(id);
        fetchEnrolledTutors(id);
    } else {
        error.value = 'No profile ID provided';
    }
});

// enrolledTutors is populated by fetchEnrolledTutors
const preferredSubjects = computed(() => profile.value?.interestedSubjects ?? []);
</script>

<style scoped>
.banner-responsive {
    height: 500px;
    background-color: transparent;
}

.overlap-section {
    position: relative;
    margin-top: -4rem;
    background: transparent;
    padding: 0 1rem;
    margin-left: auto;
    margin-right: auto;
}

.profile-image {
    width: 160px;
    height: 160px;
    border-radius: 50%;
    border: 4px solid white;
    box-shadow: 0 4px 15px rgba(0, 0, 0, 0.15);
    object-fit: cover;
    margin-top: -10px;
}

.profile-info {
    display: flex;
    flex-direction: column;
    margin-top: 40px;
}

.orange-button-small {
    margin-top: 40px;
}

@media (max-width: 767px) {
    .overlap-section {
        margin-top: -3rem;
        padding: 0 0.5rem;
    }

    .profile-image {
        width: 120px;
        height: 120px;
        margin-top: -5px;
    }

    .profile-info {
        margin-top: 15px;
    }

    .orange-button-small {
        margin-top: 15px;
    }

    .banner-responsive {
        height: 350px;
        object-fit: contain !important;
    }
}

/* Tablet Styles */
@media (min-width: 768px) and (max-width: 1023px) {
    .overlap-section {
        margin-top: -3.5rem;
    }

    .profile-image {
        width: 140px;
        height: 140px;
    }
}

.enrolled-card {
    min-height: 320px;
}

.enrolled-card-image {
    flex-shrink: 0;
    height: 180px;
    width: 100%;
    overflow: hidden;
}

.enrolled-card-img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    object-position: center;
    display: block;
}

.enrolled-card-bottom {
    padding-top: 10px;
    border-top: 1px solid #f3f4f6;
}

.enrolled-tutor-name {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.enrolled-tutor-lang {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.enrolled-subject-chip {
    display: inline-flex;
    align-items: center;
    padding: 3px 6px;
    border-radius: 20px;
    border: 1px solid #d1d5db;
    background: #f3f4f6;
    color: #6b7280;
    font-size: 10px;
    font-weight: 400;
    line-height: 1;
}
</style>